import { useMemo, useState } from 'react';
import { newId, computeCoverage, type AgendaParticipant, type RecurrenceRule, type WeekItem } from '@familyhub/shared';
import { Sheet } from '../platform/Sheet';
import { AttributeFields } from '../platform/AttributeFields';
import { DateField, TimeField } from '../platform/DateField';
import { isoToDateInput, isoToTimeInput, inputsToIso, durationMinutes, formatDuration } from './datetime';
import { saveActivity, deleteActivity, saveChildcare, deleteChildcare } from './actions';
import { parentColor, CAREGIVER_COLOR } from './labels';
import type { ActivityRecord, ChildcareRecord } from './db';

export interface Member {
  id: string;
  display_name: string;
}
interface Person {
  id: string;
  name: string;
  color?: string | null;
}

interface Common {
  members: Member[];
  people: Person[];
  /** The current week's expanded items — the warnings below read them live. */
  items: WeekItem[];
  userId: string;
  defaultDay: string;
  onClose: () => void;
  /** Present only while creating: lets a new item change its own type. */
  onSwitchKind?: (kind: AddKind) => void;
}

/* ---------------- small form pieces ---------------- */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="sheet-field">
      <span className="sheet-label">{label}</span>
      {children}
    </label>
  );
}

/* ---------------- time span: start, end, duration, multi-day ---------------- */

export interface Span {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

/** A span from a stored row (or the defaults for a new one — end = start + 1u). */
export function makeSpan(
  record: { starts_at: string; ends_at: string | null } | undefined,
  defaultDay: string,
  defaultStart = '09:00',
  defaultDurationMin = 60,
): Span {
  const startDate = record ? isoToDateInput(record.starts_at) : defaultDay;
  const startTime = record ? isoToTimeInput(record.starts_at) : defaultStart;
  const endIso =
    record?.ends_at ??
    new Date(Date.parse(inputsToIso(startDate, startTime)) + defaultDurationMin * 60_000).toISOString();
  return { startDate, startTime, endDate: isoToDateInput(endIso), endTime: isoToTimeInput(endIso) };
}

/**
 * The rule this editor saves. It only offers "every N weeks", but a series can
 * also arrive from the shared calendar as daily, monthly or yearly
 * (docs/CALENDAR_SYNC.md); saving an unrelated edit must not turn a monthly
 * series into a weekly one, so everything but the interval is kept.
 */
function repeatRule(existing: RecurrenceRule | null | undefined, every: number): RecurrenceRule {
  if (existing && existing.freq !== 'weekly') return existing;
  return {
    ...existing,
    freq: 'weekly',
    interval: every,
    until: existing?.until ?? null,
    skip: existing?.skip ?? [],
    exceptions: existing?.exceptions ?? [],
  };
}

export function spanStartIso(s: Span, allDay: boolean): string {
  return inputsToIso(s.startDate, allDay ? '00:00' : s.startTime);
}
/** The `YYYY-MM-DD` after `key`, in local time. */
function nextDayKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return isoToDateInput(new Date(y!, m! - 1, d! + 1).toISOString());
}
/**
 * The stored `ends_at`. A single all-day event keeps the module's "null = whole
 * day" convention (§3); a multi-day all-day event ends at the start of the day
 * *after* its last day, so the end date reads inclusively ("Tot wo" includes
 * Wednesday). A timed event ends at its own end instant.
 */
export function spanEndIso(s: Span, allDay: boolean): string | null {
  if (allDay) return s.endDate > s.startDate ? inputsToIso(nextDayKey(s.endDate), '00:00') : null;
  return inputsToIso(s.endDate, s.endTime);
}

const DUR_STEPS = [-30, -15, 15, 30, 60] as const;

/**
 * Start and end of an event, with the duration made cheap to set (2026-08-20
 * feedback): moving the start keeps the duration, quick chips add or subtract
 * time, and a separate end **date** lets an event span days. All-day collapses
 * to two date fields (a multi-day trip is start date → end date).
 */
function SpanFields({ span, setSpan, allDay }: { span: Span; setSpan: (s: Span) => void; allDay: boolean }) {
  const durMin = durationMinutes(inputsToIso(span.startDate, span.startTime), inputsToIso(span.endDate, span.endTime));
  // Open already when the row being edited really does span days; otherwise the
  // field stays behind a chip (see below).
  const [spansDays, setSpansDays] = useState(span.endDate !== span.startDate);

  const moveStart = (startDate: string, startTime: string) => {
    // Keep the current duration: shift the end by however the start moved.
    const endMs = Date.parse(inputsToIso(startDate, startTime)) + Math.max(0, durMin) * 60_000;
    const end = new Date(endMs).toISOString();
    setSpan({ startDate, startTime, endDate: isoToDateInput(end), endTime: isoToTimeInput(end) });
  };
  const setDuration = (mins: number) => {
    const end = new Date(Date.parse(inputsToIso(span.startDate, span.startTime)) + Math.max(15, mins) * 60_000).toISOString();
    setSpan({ ...span, endDate: isoToDateInput(end), endTime: isoToTimeInput(end) });
  };

  if (allDay) {
    return (
      <div className="sheet-row">
        <Field label="Van">
          <DateField
            value={span.startDate}
            onChange={(startDate) =>
              setSpan({ ...span, startDate, endDate: startDate > span.endDate ? startDate : span.endDate })
            }
          />
        </Field>
        <Field label="Tot">
          <DateField
            value={span.endDate}
            min={span.startDate}
            onChange={(endDate) => setSpan({ ...span, endDate: endDate < span.startDate ? span.startDate : endDate })}
          />
        </Field>
      </div>
    );
  }

  return (
    <>
      <div className="sheet-row">
        <Field label="Datum">
          <DateField value={span.startDate} onChange={(d) => moveStart(d, span.startTime)} />
        </Field>
        <Field label="Van">
          <TimeField value={span.startTime} onChange={(t) => moveStart(span.startDate, t)} />
        </Field>
      </div>
      <div className="sheet-row">
        {/* The end *date* only when it is actually a different day. It was
            always on screen, sitting under the start date holding an identical
            value, so the overwhelmingly common case — an hour at the dentist —
            paid a whole labelled field for nothing. The chip occupies the slot
            its field will fill, so nothing moves when it opens. */}
        {spansDays ? (
          <Field label="Einddatum">
            <DateField
              value={span.endDate}
              min={span.startDate}
              onChange={(endDate) => setSpan({ ...span, endDate })}
            />
          </Field>
        ) : (
          <div className="sheet-field">
            <span className="sheet-label">&nbsp;</span>
            <button
              type="button"
              className="chip attr-chip"
              onClick={() => setSpansDays(true)}
            >
              + meerdere dagen
            </button>
          </div>
        )}
        <Field label="Tot">
          <TimeField value={span.endTime} onChange={(endTime) => setSpan({ ...span, endTime })} />
        </Field>
      </div>
      <div className="sheet-field">
        {/* The value is outside the uppercasing: `.sheet-label` sets
            text-transform, which turned "1u 30m" into "1U 30M". */}
        <span className="sheet-label">
          Duur · <span className="sheet-label-value">{formatDuration(durMin)}</span>
        </span>
        <div className="chips chips-inline">
          {DUR_STEPS.map((d) => (
            <button
              key={d}
              type="button"
              className="chip"
              aria-label={`${d > 0 ? 'langer' : 'korter'} ${Math.abs(d)} minuten`}
              onClick={() => setDuration(durMin + d)}
            >
              {d > 0 ? `+${d === 60 ? '1u' : `${d}m`}` : `−${Math.abs(d)}m`}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function RecurRow({
  recurring,
  setRecurring,
  every,
  setEvery,
}: {
  recurring: boolean;
  setRecurring: (v: boolean) => void;
  every: number;
  setEvery: (v: number) => void;
}) {
  return (
    <label className="sheet-row" style={{ gap: 8 }}>
      <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
      <span>Herhaalt wekelijks</span>
      {recurring && (
        <>
          <span style={{ color: 'var(--text-dim)' }}>elke</span>
          <input type="number" min={1} max={52} value={every} onChange={(e) => setEvery(Math.max(1, Number(e.target.value) || 1))} style={{ width: 64 }} />
          <span style={{ color: 'var(--text-dim)' }}>week/weken</span>
        </>
      )}
    </label>
  );
}

function Actions({ onSave, canSave, onDelete }: { onSave: () => void; canSave: boolean; onDelete?: () => void }) {
  return (
    <div className="sheet-row" style={{ marginTop: 4 }}>
      <button className="btn btn-primary" onClick={onSave} disabled={!canSave}>
        Opslaan
      </button>
      {onDelete && (
        <button className="btn btn-danger" onClick={onDelete}>
          Verwijderen
        </button>
      )}
    </div>
  );
}

function ParticipantPicker({
  members,
  people = [],
  value,
  onChange,
  label,
  single = false,
}: {
  members: Member[];
  people?: Person[];
  value: AgendaParticipant[];
  onChange: (v: AgendaParticipant[]) => void;
  label: string;
  single?: boolean;
}) {
  const memberIds = members.map((m) => m.id);
  const options: (AgendaParticipant & { name: string; color: string })[] = [
    ...members.map((m) => ({ kind: 'user' as const, id: m.id, name: m.display_name, color: parentColor(m.id, memberIds) })),
    ...people.map((p) => ({ kind: 'person' as const, id: p.id, name: p.name, color: p.color || CAREGIVER_COLOR })),
  ];
  const has = (o: AgendaParticipant) => value.some((v) => v.kind === o.kind && v.id === o.id);
  const toggle = (o: AgendaParticipant) => {
    if (single) return onChange(has(o) ? [] : [{ kind: o.kind, id: o.id }]);
    onChange(has(o) ? value.filter((v) => !(v.kind === o.kind && v.id === o.id)) : [...value, o]);
  };

  return (
    <div className="sheet-field">
      <span className="sheet-label">{label}</span>
      <div className="chips chips-inline">
        {options.map((o) => (
          <button
            key={`${o.kind}:${o.id}`}
            type="button"
            // `chip-person`: a chosen person wears their *own* colour, not the
            // app's green. Green is the one high-emphasis voice on a screen, and
            // this sheet was spending it six times over (Hele dag, both
            // participants, Auto, Kleine, Notitie) before the green Opslaan
            // button could use it. A person's identity colour is also simply the
            // truer answer here — the picker is about who, and who already has
            // a colour everywhere else in the module.
            className="chip chip-dot chip-person"
            style={{ '--chip-color': o.color } as React.CSSProperties}
            aria-pressed={has(o)}
            onClick={() => toggle({ kind: o.kind, id: o.id })}
          >
            <span className="agenda-person-dot" style={{ background: o.color }} aria-hidden />
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------- activity ---------------- */

export function ActivitySheet({
  record,
  members,
  items,
  userId,
  defaultDay,
  onClose,
  onSwitchKind,
}: Common & { record?: ActivityRecord }) {
  const [title, setTitle] = useState(record?.title ?? '');
  const [allDay, setAllDay] = useState(record?.all_day ?? false);
  const [span, setSpan] = useState<Span>(() => makeSpan(record, defaultDay));
  const [participants, setParticipants] = useState<AgendaParticipant[]>(
    record?.participants ?? (userId ? [{ kind: 'user', id: userId }] : []),
  );
  const [needsCar, setNeedsCar] = useState(record?.needs_car ?? false);
  const [bringsChild, setBringsChild] = useState(record?.brings_child ?? false);
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [recurring, setRecurring] = useState(!!record?.recurrence);
  const [every, setEvery] = useState(record?.recurrence?.interval ?? 1);

  /**
   * Two non-blocking warnings (docs/AGENDA.md §6), computed together from the
   * same draft item so they share the day's other items: whether the window
   * still has nobody explicitly down for the child — nobody is ever assumed to
   * have the child just because a parent happens to be free (2026-08-19
   * feedback), and *unless the child is coming along* — and whether a
   * participant is also the assigned caregiver for an overlapping childcare
   * window, who therefore can't be in both places.
   */
  const draftWarnings = useMemo(() => {
    const starts_at = spanStartIso(span, allDay);
    const ends_at = allDay ? null : spanEndIso(span, allDay);
    const day = span.startDate;
    const draft: WeekItem = {
      key: 'draft:activity',
      rowId: record?.id ?? 'draft',
      type: 'activity',
      title: title || '',
      starts_at,
      ends_at,
      all_day: allDay,
      day,
      personKeys: participants.map((p) => `${p.kind}:${p.id}`),
      needsCar,
      bringsChild,
    };
    const dayItems = items.filter((i) => i.day === day && !(i.type === 'activity' && i.rowId === record?.id));
    const cov = computeCoverage([...dayItems, draft], [day]);
    const coverage = cov.activityCoverage['draft:activity']?.state ?? 'covered';
    let clashName: string | null = null;
    if (cov.conflictKeys.includes('draft:activity')) {
      const clash = dayItems.find((i) => i.type === 'childcare' && cov.conflictKeys.includes(i.key));
      const clashKey = participants.map((p) => `user:${p.id}`).find((k) => clash?.caregiverKeys?.includes(k));
      clashName = clashKey ? members.find((m) => `user:${m.id}` === clashKey)?.display_name ?? null : null;
    }
    return { coverage, clashName };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [span, allDay, participants, needsCar, bringsChild, items, record?.id, members]);

  const save = () => {
    if (!title.trim()) return;
    saveActivity({
      id: record?.id ?? newId(),
      title: title.trim(),
      starts_at: spanStartIso(span, allDay),
      ends_at: spanEndIso(span, allDay),
      all_day: allDay,
      needs_car: needsCar,
      brings_child: bringsChild,
      notes: notes.trim() || null,
      participants: participants.map((p) => ({ kind: 'user' as const, id: p.id })),
      recurrence: recurring
        ? repeatRule(record?.recurrence, every)
        : null,
      created_by: record?.created_by ?? userId ?? null,
    });
    onClose();
  };

  return (
    <Sheet title={record ? 'Activiteit' : 'Toevoegen'} onClose={onClose}>
      {!record && onSwitchKind && <KindSwitch kind="activity" onSwitch={onSwitchKind} />}
      {!bringsChild && draftWarnings.coverage !== 'covered' && (
        <div className="banner">
          Let op: {draftWarnings.coverage === 'uncovered' ? 'er is nog niemand bij de kleine' : 'de opvang is nog niet voor de hele periode geregeld'}.
        </div>
      )}
      {draftWarnings.clashName && (
        <div className="banner">Let op: {draftWarnings.clashName} staat dan ook ingepland als oppas.</div>
      )}
      <Field label="Titel">
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Tandarts" autoFocus />
      </Field>
      <div className="attr-chips">
        <button
          type="button"
          className={`chip attr-chip${allDay ? ' attr-chip-set' : ''}`}
          aria-pressed={allDay}
          onClick={() => setAllDay(!allDay)}
        >
          Hele dag
        </button>
      </div>
      <SpanFields span={span} setSpan={setSpan} allDay={allDay} />
      <ParticipantPicker members={members} value={participants} onChange={setParticipants} label="Wie" />
      <AttributeFields
        fields={[
          { key: 'car', label: '🚗 Auto nodig', set: needsCar, onToggle: () => setNeedsCar(!needsCar) },
          { key: 'child', label: '👶 Kleine gaat mee', set: bringsChild, onToggle: () => setBringsChild(!bringsChild) },
          {
            key: 'note',
            label: 'Notitie',
            set: !!notes.trim(),
            panel: (
              <Field label="Notitie">
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optioneel" />
              </Field>
            ),
          },
          {
            key: 'repeat',
            label: 'Herhalen',
            set: recurring,
            value: recurring ? (every > 1 ? `Elke ${every} weken` : 'Wekelijks') : undefined,
            panel: <RecurRow recurring={recurring} setRecurring={setRecurring} every={every} setEvery={setEvery} />,
          },
        ]}
      />
      <Actions onSave={save} canSave={!!title.trim()} onDelete={record ? () => { deleteActivity(record.id); onClose(); } : undefined} />
    </Sheet>
  );
}

/* ---------------- childcare ---------------- */

export function ChildcareSheet({
  record,
  members,
  people,
  items,
  defaultDay,
  onClose,
  userId,
  onSwitchKind,
}: Common & { record?: ChildcareRecord }) {
  const [caregivers, setCaregivers] = useState<AgendaParticipant[]>(record?.caregivers ?? []);
  const [wholeDay, setWholeDay] = useState(record ? !record.ends_at : false);
  const [wholeDate, setWholeDate] = useState(record ? isoToDateInput(record.starts_at) : defaultDay);
  const [span, setSpan] = useState<Span>(() => makeSpan(record, defaultDay, '09:00', 8 * 60));
  const [recurring, setRecurring] = useState(!!record?.recurrence);
  const [every, setEvery] = useState(record?.recurrence?.interval ?? 1);
  const [notes, setNotes] = useState(record?.notes ?? '');

  const starts_at = wholeDay ? inputsToIso(wholeDate, '00:00') : spanStartIso(span, false);
  const ends_at = wholeDay ? null : spanEndIso(span, false);
  const day = wholeDay ? wholeDate : span.startDate;

  /**
   * A household-member caregiver who also has an overlapping activity is the
   * other non-blocking warning docs/AGENDA.md §6 asks for — a caregiver can't be
   * in two places. Computed with the same coverage engine as ActivitySheet, now
   * over *all* the chosen caregivers, so it names whichever one clashes.
   */
  const caregiverClash = useMemo(() => {
    const userKeys = caregivers.filter((c) => c.kind === 'user').map((c) => `user:${c.id}`);
    if (userKeys.length === 0) return null;
    const keys = caregivers.map((c) => `${c.kind}:${c.id}`);
    const draft: WeekItem = {
      key: 'draft:childcare',
      rowId: record?.id ?? 'draft',
      type: 'childcare',
      title: '',
      starts_at,
      ends_at,
      all_day: wholeDay,
      day,
      personKeys: keys,
      caregiverKeys: keys,
    };
    const dayItems = items.filter((i) => i.day === day && !(i.type === 'childcare' && i.rowId === record?.id));
    const cov = computeCoverage([...dayItems, draft], [day]);
    if (!cov.conflictKeys.includes('draft:childcare')) return null;
    const clash = dayItems.find((i) => i.type === 'activity' && cov.conflictKeys.includes(i.key));
    if (!clash) return null;
    const clashKey = userKeys.find((k) => clash.personKeys.includes(k));
    const who = caregivers.find((c) => `user:${c.id}` === clashKey);
    return { name: (who && members.find((m) => m.id === who.id)?.display_name) || 'deze persoon', title: clash.title };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caregivers, starts_at, ends_at, wholeDay, day, items, record?.id, members]);

  const save = () => {
    if (caregivers.length === 0) return;
    saveChildcare({
      id: record?.id ?? newId(),
      caregivers,
      starts_at,
      ends_at,
      recurrence: recurring
        ? repeatRule(record?.recurrence, every)
        : null,
      notes: notes.trim() || null,
      created_by: record?.created_by ?? userId ?? null,
    });
    onClose();
  };

  return (
    <Sheet title={record ? 'Opvang' : 'Toevoegen'} onClose={onClose}>
      {!record && onSwitchKind && <KindSwitch kind="childcare" onSwitch={onSwitchKind} />}
      {caregiverClash && (
        <div className="banner">
          Let op: {caregiverClash.name} heeft dan ook: {caregiverClash.title}.
        </div>
      )}
      <ParticipantPicker members={members} people={people} value={caregivers} onChange={setCaregivers} label="Wie heeft de kleine" />
      <div className="attr-chips">
        <button
          type="button"
          className={`chip attr-chip${wholeDay ? ' attr-chip-set' : ''}`}
          aria-pressed={wholeDay}
          onClick={() => setWholeDay(!wholeDay)}
        >
          Hele dag
        </button>
      </div>
      {wholeDay ? (
        <Field label="Datum">
          <DateField value={wholeDate} onChange={setWholeDate} />
        </Field>
      ) : (
        <SpanFields span={span} setSpan={setSpan} allDay={false} />
      )}
      <AttributeFields
        fields={[
          {
            key: 'repeat',
            label: 'Herhalen',
            set: recurring,
            value: recurring ? (every > 1 ? `Elke ${every} weken` : 'Wekelijks') : undefined,
            panel: <RecurRow recurring={recurring} setRecurring={setRecurring} every={every} setEvery={setEvery} />,
          },
          {
            key: 'note',
            label: 'Notitie',
            set: !!notes.trim(),
            panel: (
              <Field label="Notitie">
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optioneel" />
              </Field>
            ),
          },
        ]}
      />
      <Actions onSave={save} canSave={caregivers.length > 0} onDelete={record ? () => { deleteChildcare(record.id); onClose(); } : undefined} />
    </Sheet>
  );
}

/* ---------------- the type switcher ---------------- */

export type AddKind = 'activity' | 'childcare';

/**
 * Activiteit or Opvang, at the head of a *new* item's sheet.
 *
 * This replaced a full scrim'd `AddChooser` sheet whose entire content was two
 * words. Recording "Oma has Tuesday afternoon" cost two modals: tap +, pick
 * Opvang, then finally the form — and the day's + opened that chooser while the
 * FAB opened an activity directly, so one glyph meant two different things on
 * one screen. Both now open the same sheet and the choice lives inside it, one
 * tap away and visible rather than hidden behind a step.
 *
 * Only on a new item: an existing row cannot change type (the two live in
 * different tables), so offering it would be a lie.
 */
function KindSwitch({ kind, onSwitch }: { kind: AddKind; onSwitch: (k: AddKind) => void }) {
  const options: [AddKind, string][] = [
    ['activity', 'Activiteit'],
    ['childcare', 'Opvang'],
  ];
  return (
    <div className="kind-switch" role="group" aria-label="Wat voeg je toe?">
      {options.map(([k, label]) => (
        <button
          key={k}
          type="button"
          className="kind-switch-option"
          aria-pressed={k === kind}
          onClick={() => k !== kind && onSwitch(k)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
